import { Alert, AlertTitle, Skeleton, Stack } from '@cmg/design-system';
import { useMemo } from 'react';

import ErrorRenderer from '../../../common/components/errorRenderer/Error-Renderer';
import { useMatchingCmgEntityKeys } from '../../../common/hooks/useMatchingCmgEntityKeys';
import { sellingGroupManagerRoles } from '../../../types/domain/manager/constants';
import { OfferingProfile } from '../../offering-profile-mui/OfferingProfile';
import { useOfferingSidePanelOfferingId } from '../hooks/useOfferingSidePanelMatch';
import { OfferingDetailsLayout } from './components/OfferingDetailsLayout';
import { useOfferingDetails_PublishedOfferingQuery } from './graphql/__generated__';

function SellSideOfferingDetailsRoute() {
  const offeringId = useOfferingSidePanelOfferingId();
  const matchingCmgEntityKeys = useMatchingCmgEntityKeys();
  const { data, loading, error } = useOfferingDetails_PublishedOfferingQuery({
    variables: { offeringId },
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });

  const [offering] = data?.publishedOfferings?.items ?? [];

  const managersWithoutSellingGroupRoles = useMemo(() => {
    return (
      offering?.syndicate?.managers.filter(manager => {
        return !sellingGroupManagerRoles.includes(manager.role);
      }) ?? []
    );
  }, [offering?.syndicate?.managers]);

  if (loading) {
    return (
      <Stack spacing={1} role="progressbar">
        <Skeleton />
        <Skeleton />
        <Skeleton />
        <Skeleton variant="rectangular" height={118} />
      </Stack>
    );
  }

  if (!offering) {
    return (
      <OfferingDetailsLayout>
        <Alert severity="error">
          <AlertTitle>This Offering could not be found.</AlertTitle>
          This Offering ID is no longer valid. The offering may have been terminated, withdrawn, or
          deleted. Please <a href="mailto:support@cmgx.io">contact CMG support</a> for more
          information.
        </Alert>
      </OfferingDetailsLayout>
    );
  }

  if (
    !managersWithoutSellingGroupRoles.some(manager =>
      matchingCmgEntityKeys.includes(manager.cmgEntityKey)
    )
  ) {
    return (
      <OfferingDetailsLayout>
        <Alert severity="warning">
          <AlertTitle>Your firm is not participating as an Underwriter on this offering</AlertTitle>
          Offering Details are only available to participating underwriter firms on an offering.
        </Alert>
      </OfferingDetailsLayout>
    );
  }

  if (error) {
    return <ErrorRenderer error={error} />;
  }

  return (
    <OfferingDetailsLayout>{data && <OfferingProfile offering={offering} />}</OfferingDetailsLayout>
  );
}

export default SellSideOfferingDetailsRoute;
